import { Component, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoPipe } from '@jsverse/transloco';
import { LucideAngularModule, Palette, Check } from 'lucide-angular';

import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { THEMES, ThemeName, ThemeService } from '../../core/services/theme.service';

/**
 * The colour theme, from the top bar.
 *
 * A menu rather than a native select, because the point is to see the
 * colours: each row carries a swatch drawn by the theme itself — the swatch
 * has its own `data-theme`, so `bg-primary` on it is that theme's primary,
 * and no colour is written down here a second time.
 *
 * Switching is immediate and local; saving to the account follows quietly,
 * exactly as the language does.
 */
@Component({
  selector: 'app-theme-switcher',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TranslocoPipe],
  template: `
    <div class="relative">
      <button #trigger type="button" (click)="toggle()"
        [attr.aria-label]="'shell.theme' | transloco"
        [title]="'shell.theme' | transloco"
        aria-haspopup="menu" [attr.aria-expanded]="open"
        class="p-2 rounded-control text-mutedText hover:bg-gray-50 hover:text-slateText transition-colors">
        <lucide-icon [img]="Palette" class="w-5 h-5"></lucide-icon>
      </button>

      @if (open) {
        <div role="menu" [attr.aria-label]="'shell.theme' | transloco"
          class="absolute right-0 mt-2 w-48 z-50 kai-dialog shadow-menu p-1.5"
          (keydown)="onMenuKey($event)">
          <p class="px-2.5 pt-1 pb-1.5 text-label-sm uppercase text-faintText">{{ 'shell.theme' | transloco }}</p>
          @for (name of themes; track name) {
            <button type="button" role="menuitemradio" [attr.aria-checked]="name === theme.current()"
              [attr.data-theme-option]="name"
              (click)="choose(name)"
              class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-control text-sm text-left transition-colors hover:bg-gray-50"
              [ngClass]="name === theme.current() ? 'font-semibold text-slateText' : 'text-mutedText'">
              <!-- Drawn by the theme it names: data-theme scopes the variables to this dot. -->
              <span [attr.data-theme]="name" class="w-4 h-4 rounded-full bg-primary ring-2 ring-primary-tint shrink-0"></span>
              <span class="flex-1">{{ ('theme.' + name) | transloco }}</span>
              @if (name === theme.current()) {
                <lucide-icon [img]="Check" class="w-4 h-4 text-primary shrink-0"></lucide-icon>
              }
            </button>
          }
        </div>
      }
    </div>
  `
})
export class ThemeSwitcherComponent {
  readonly theme = inject(ThemeService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;

  readonly Palette = Palette;
  readonly Check = Check;
  readonly themes = THEMES;

  open = false;

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      // Land on the theme in force, so Enter on an unchanged menu is a no-op.
      setTimeout(() => this.items()[Math.max(THEMES.indexOf(this.theme.current()), 0)]?.focus());
    }
  }

  choose(name: ThemeName): void {
    this.theme.use(name);
    this.close();
    // Remember it on the account, so the next device starts here too. Nobody
    // is told if this fails: the colours already changed, which is what was
    // asked for, and a toast about a preference would be noise.
    if (this.auth.isAuthenticated()) {
      this.api.updateMyProfile({ theme: name }).subscribe({
        error: (error: HttpErrorResponse) => console.warn('Theme not saved', error.status)
      });
    }
  }

  /** Up and down move between themes; Escape and Tab leave. */
  onMenuKey(event: KeyboardEvent): void {
    const items = this.items();
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
    } else if (event.key === 'Tab') {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
    }
  }

  /** A click anywhere else closes the menu, as a menu should. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  private close(): void {
    this.open = false;
    this.trigger?.nativeElement.focus();
  }

  private items(): HTMLButtonElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]'));
  }
}
